import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  Bell,
  Building2,
  ChevronRight,
  Droplets,
  Gauge,
  Layers,
  LockKeyhole,
  Mail,
  MapPin,
  Palette,
  Settings,
  Smartphone,
  UserRound,
} from 'lucide-react';

import { useAuth } from '../contexts/AuthContext';
import { useAgriSafeSoverdiDashboard } from '../hooks/useAgriSafeSoverdiDashboard';
import SoverdiAlertSettingsR7 from './SoverdiAlertSettingsR7';
import SoverdiEquipmentSettingsR7 from './SoverdiEquipmentSettingsR7';
import SoverdiFarmSettingsR7 from './SoverdiFarmSettingsR7';
import SoverdiPreferencesR7 from './SoverdiPreferencesR7';
import SoverdiProfileSecurityR7 from './SoverdiProfileSecurityR7';
import SoverdiSectorNamingR7 from './SoverdiSectorNamingR7';

const roleLabel = (role: string): string => {
  const labels: Record<string, string> = {
    owner: i18n.t("sectorViews.Proprietaire"),
    admin: 'Administrateur',
    coop_admin: 'Administrateur',
    org_admin: 'Administrateur',
    farm_manager: i18n.t("sectorViews.Responsable_d_exploitation"),
    farmer: 'Exploitant agricole',
    viewer: 'Consultation',
  };
  return labels[role] || role;
};

const formatArea = (value: number): string =>
  `${value.toLocaleString(i18n.language, { maximumFractionDigits: 2 })} ha`;

const formatCapacity = (value: number | null): string =>
  value === null
    ? '—'
    : `${value.toLocaleString(i18n.language, { maximumFractionDigits: 2 })} m³/h`;

interface SummaryCardProps {
  label: string;
  value: string | number;
  icon: React.ElementType;
}

const SummaryCard: React.FC<SummaryCardProps> = ({ label, value, icon: Icon }) => (
  <div className="rounded-xl border border-[var(--border-light)] bg-[var(--surface)] p-4 sm:p-5">
    <div className="flex items-center justify-between gap-3">
      <div>
        <p className="text-xs font-medium uppercase tracking-wide text-[var(--text-tertiary)]">{label}</p>
        <p className="mt-2 text-xl sm:text-2xl font-semibold text-[var(--text-primary)]">{value}</p>
      </div>
      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[var(--color-primary)]/10">
        <Icon className="h-5 w-5 text-[var(--color-primary)]" />
      </div>
    </div>
  </div>
);

interface ActionRowProps {
  title: string;
  description: string;
  icon: React.ElementType;
  onClick: () => void;
  testId: string;
}

const ActionRow: React.FC<ActionRowProps> = ({ title, description, icon: Icon, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className="group flex min-h-[64px] w-full items-center gap-3 rounded-xl border border-[var(--border-light)] bg-[var(--surface)] px-4 py-3 text-left transition-colors hover:bg-[var(--gray-100)] active:bg-[var(--gray-200)]"
  >
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--color-primary)]/10">
      <Icon className="h-5 w-5 text-[var(--color-primary)]" />
    </div>
    <div className="min-w-0 flex-1">
      <p className="font-semibold text-[var(--text-primary)]">{title}</p>
      <p className="mt-0.5 text-sm text-[var(--text-secondary)]">{description}</p>
    </div>
    <ChevronRight className="h-5 w-5 shrink-0 text-[var(--text-tertiary)] transition-transform group-hover:translate-x-0.5" />
  </button>
);

const SoverdiSettingsOverviewR7: React.FC = () => {
  useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { topology, sectors: legacySectors, operationalSectors, bindingsUnavailable, loading, loadError } = useAgriSafeSoverdiDashboard();
  const sectors = operationalSectors ?? legacySectors ?? [];
  const inventoryUnavailable = bindingsUnavailable && topology?.sites.some(site => site.name === 'Roubia');

  const topologyUnavailable = !loading && (loadError || !topology);

  return (
    <div className="space-y-5 sm:space-y-6" data-testid="soverdi-settings-r7">
      <section className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-[var(--color-primary)]">
            <Settings className="h-4 w-4" />
            <span className="text-xs font-semibold uppercase tracking-wide">{i18n.t("sectorViews.Compte_client")}</span>
          </div>
          <h2 className="mt-1 text-xl sm:text-2xl font-semibold text-[var(--text-primary)]">{i18n.t("settings")}</h2>
          <p className="mt-1 max-w-2xl text-sm text-[var(--text-secondary)]">
            {i18n.t("sectorViews.Informations_de_votre_exploitation_compte_utilisateur_et_acces_aux_con")}</p>
        </div>
        <span className="inline-flex w-fit items-center rounded-full border border-[var(--border-light)] bg-[var(--surface)] px-3 py-1.5 text-xs font-medium text-[var(--text-secondary)]">
          {i18n.t("sectorViews.Espace_client")}</span>
      </section>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <section className="rounded-2xl border border-[var(--border-light)] bg-[var(--surface)] p-5 sm:p-6">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[var(--color-primary)]/10">
              <Building2 className="h-5 w-5 text-[var(--color-primary)]" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-wide text-[var(--text-tertiary)]">{i18n.t("exploitation")}</p>
              {loading ? (
                <div className="mt-2 h-7 w-56 max-w-full animate-pulse rounded bg-[var(--gray-100)]" data-testid="settings-farm-loading" />
              ) : topologyUnavailable ? (
                <p className="mt-2 font-semibold text-[var(--text-primary)]">{i18n.t("sectorViews.Informations_indisponibles")}</p>
              ) : (
                <h3 className="mt-1 truncate text-lg font-semibold text-[var(--text-primary)]" data-testid="settings-farm-name">
                  {topology?.clientName || '—'}
                </h3>
              )}
              <p className="mt-1 text-sm text-[var(--text-secondary)]">
                {i18n.t("sectorViews.Les_identites_techniques_et_les_liaisons_terrain_restent_gerees_par_Ag")}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate('/r6-soverdi-settings?section=farm-sites')}
            className="mt-5 min-h-[44px] w-full rounded-xl border border-[var(--border-light)] px-4 py-2.5 text-sm font-semibold text-[var(--color-primary)] transition-colors hover:bg-[var(--gray-100)]"
            data-testid="settings-open-farm-sites"
          >
            {i18n.t("sectorViews.Exploitation_sites")}</button>
        </section>

        <section className="rounded-2xl border border-[var(--border-light)] bg-[var(--surface)] p-5 sm:p-6">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[var(--color-primary)]/10">
              <UserRound className="h-5 w-5 text-[var(--color-primary)]" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold uppercase tracking-wide text-[var(--text-tertiary)]">{i18n.t("sectorViews.Mon_compte")}</p>
              <h3 className="mt-1 truncate text-lg font-semibold text-[var(--text-primary)]" data-testid="settings-user-name">
                {user?.name || '—'}
              </h3>
              <div className="mt-3 space-y-2 text-sm text-[var(--text-secondary)]">
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 shrink-0" />
                  <span className="truncate" data-testid="settings-user-email">{user?.email || '—'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <LockKeyhole className="h-4 w-4 shrink-0" />
                  <span>{user?.role ? roleLabel(user.role) : '—'}</span>
                </div>
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate('/r6-soverdi-settings?section=security')}
            className="mt-5 min-h-[44px] w-full rounded-xl border border-[var(--border-light)] px-4 py-2.5 text-sm font-semibold text-[var(--color-primary)] transition-colors hover:bg-[var(--gray-100)]"
            data-testid="settings-open-profile-security"
          >
            {i18n.t("sectorViews.Profil_securite")}</button>
        </section>
      </div>

      {topologyUnavailable && (
        <section
          className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-amber-900"
          role="status"
          data-testid="settings-topology-error"
        >
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
          <div>
            <p className="font-semibold">{i18n.t("sectorViews.Configuration_de_l_exploitation_indisponible")}</p>
            <p className="mt-1 text-sm">
              {i18n.t("sectorViews.Les_informations_n_ont_pas_pu_etre_chargees_Aucun_compteur_n_est_rempl")}</p>
          </div>
        </section>
      )}

      {!loading && topology && (
        <>
          <section>
            <div className="mb-3">
              <h3 className="text-lg font-semibold text-[var(--text-primary)]">{i18n.t("sectorViews.Vue_d_ensemble_de_l_exploitation")}</h3>
              <p className="mt-1 text-sm text-[var(--text-secondary)]">{i18n.t("sectorViews.Donnees_issues_de_la_topologie_Agri_Safe_actuellement_affectee_a_votre")}</p>
            </div>
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <SummaryCard label={i18n.t("sectorViews.Surface")} value={formatArea(topology.summary.areaHa)} icon={MapPin} />
              <SummaryCard label={i18n.t("sectorViews.Sites")} value={topology.summary.siteCount} icon={Building2} />
              <SummaryCard label={i18n.t("sectorViews.Secteurs")} value={inventoryUnavailable ? i18n.t("availabilityUnavailable") : sectors.length} icon={Layers} />
              <SummaryCard label={i18n.t("sectorViews.Parcelles")} value={topology.summary.parcelCount} icon={Droplets} />
            </div>
          </section>

          <section className="rounded-2xl border border-[var(--border-light)] bg-[var(--surface)] overflow-hidden">
            <div className="border-b border-[var(--border-light)] px-4 py-4 sm:px-5">
              <h3 className="font-semibold text-[var(--text-primary)]">{i18n.t("sectorViews.Sites")}</h3>
              <p className="mt-1 text-sm text-[var(--text-secondary)]">{i18n.t("sectorViews.Configuration_structurelle_connue_pour_l_exploitation")}</p>
            </div>
            <div className="divide-y divide-[var(--border-light)]">
              {topology.sites.map((site) => {
                const parcelCount = site.sectors.reduce((total, sector) => total + sector.parcels.length, 0);
                return (
                  <div key={site.name} className="grid gap-3 px-4 py-4 sm:px-5 lg:grid-cols-[1.3fr_1fr_1fr] lg:items-center" data-testid={`settings-site-${site.name}`}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 shrink-0 text-[var(--color-primary)]" />
                        <p className="font-semibold text-[var(--text-primary)]">{site.name}</p>
                      </div>
                      <p className="mt-1 truncate pl-6 text-sm text-[var(--text-secondary)]">{site.waterSource || '—'}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm lg:grid-cols-1">
                      <div>
                        <span className="text-[var(--text-tertiary)]">{i18n.t("sectorViews.Surface")}</span>
                        <span className="font-medium text-[var(--text-primary)]">{formatArea(site.areaHa)}</span>
                      </div>
                      <div>
                        <span className="text-[var(--text-tertiary)]">{i18n.t("sectorViews.Capacite")}</span>
                        <span className="font-medium text-[var(--text-primary)]">{formatCapacity(site.capacityM3h)}</span>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 lg:justify-end">
                      <span className="rounded-full bg-[var(--gray-100)] px-2.5 py-1 text-xs font-medium text-[var(--text-secondary)]">
                        {bindingsUnavailable && site.name === 'Roubia' ? i18n.t("availabilityUnavailable") : i18n.t("sectorViews.sectorCount", { count: sectors.filter(sector => sector.siteName === site.name).length })}
                      </span>
                      <span className="rounded-full bg-[var(--gray-100)] px-2.5 py-1 text-xs font-medium text-[var(--text-secondary)]">
                        {i18n.t("sectorViews.parcelCount", { count: parcelCount })}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        </>
      )}

      <section>
        <div className="mb-3">
          <h3 className="text-lg font-semibold text-[var(--text-primary)]">{i18n.t("sectorViews.Configuration_accessible")}</h3>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">
            {i18n.t("sectorViews.Ces_actions_ouvrent_des_fonctions_deja_connectees_aux_donnees_Agri_Saf")}</p>
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
          <ActionRow
            title={i18n.t("sectorViews.Noms_des_secteurs")}
            description={i18n.t("sectorViews.Personnaliser_les_noms_affiches_sans_modifier_les_liaisons_terrain")}
            icon={Layers}
            onClick={() => navigate('/r6-soverdi-settings?section=sector-names')}
            testId="settings-open-sector-names"
          />
          <ActionRow
            title={i18n.t("sectorViews.alertsNotifications")}
            description={i18n.t("sectorViews.Consulter_les_alertes_reelles_et_l_etat_des_canaux_de_notification")}
            icon={Bell}
            onClick={() => navigate('/r6-soverdi-settings?section=alerts-notifications')}
            testId="settings-open-alerts"
          />
          <ActionRow
            title={i18n.t("sectorViews.Preferences")}
            description={i18n.t("sectorViews.Choisir_la_langue_et_l_apparence_de_l_interface_sur_cet_appareil")}
            icon={Palette}
            onClick={() => navigate('/r6-soverdi-settings?section=preferences')}
            testId="settings-open-preferences"
          />
          <ActionRow
            title={i18n.t("sectorViews.Equipements_affectations")}
            description={i18n.t("sectorViews.Consulter_les_equipements_et_leur_affectation_metier_en_lecture_seule")}
            icon={Smartphone}
            onClick={() => navigate('/r6-soverdi-settings?section=equipment-assignments')}
            testId="settings-open-equipment"
          />
        </div>
      </section>

      <section className="rounded-xl border border-[var(--border-light)] bg-[var(--gray-50)] p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <Gauge className="mt-0.5 h-5 w-5 shrink-0 text-[var(--text-tertiary)]" />
          <div>
            <p className="font-semibold text-[var(--text-primary)]">{i18n.t("sectorViews.Configuration_technique_protegee")}</p>
            <p className="mt-1 text-sm text-[var(--text-secondary)]">
              {i18n.t("sectorViews.Commissioning_bindings_canaux_relais_identifiants_LoRaWAN_et_configura")}</p>
          </div>
        </div>
      </section>
    </div>
  );
};

const SoverdiSettingsR7: React.FC = () => {
  useTranslation();
  const location = useLocation();
  const section = new URLSearchParams(location.search).get('section');

  if (section === 'security') {
    return <SoverdiProfileSecurityR7 />;
  }

  if (section === 'farm-sites') {
    return <SoverdiFarmSettingsR7 />;
  }

  if (section === 'sector-names') {
    return <SoverdiSectorNamingR7 />;
  }

  if (section === 'alerts-notifications') {
    return <SoverdiAlertSettingsR7 />;
  }

  if (section === 'preferences') {
    return <SoverdiPreferencesR7 />;
  }

  if (section === 'equipment-assignments') {
    return <SoverdiEquipmentSettingsR7 />;
  }

  return <SoverdiSettingsOverviewR7 />;
};

export default SoverdiSettingsR7;
